import axios from 'axios';
import moment from 'moment';

import {
	SET_TEAM_CONVERSATIONS,
	SET_ACTIVE_TEAM_CONVERSATION,
	ADD_TEAM_CONVERSATION,
	ADD_MESSAGE_TO_TEAM_CONVERSATION,
	UPDATE_TEAM_CONVERSATION,
	REPLACE_MESSAGES_IN_TEAM_CONVERSATION,
	ADD_MESSAGES_TO_TEAM_CONVERSATION,
	REMOVE_TEAM_CONVERSATION,
	SET_ACTIVE_TEAM_CONVERSATION_TO_NULL,
	APPEND_TEAM_CONVERSATIONS,
	UPDATE_TEAM_MESSAGE,
	SET_TEAM_REPLY,
} from './types';

import getTeamsUrl from '../utils/getTeamsUrl';

export const getSurroundingMessages =
	({ _teamConversation, _teamMessage }) =>
	async (dispatch) => {
		try {
			const res = await axios.post(
				`${getTeamsUrl(
					window.location.href
				)}/api/get-conversation-messages`,
				{
					_teamConversation,
					_teamMessage,
				}
			);

			const { data } = res;

			if (data.error) {
				return { error: true };
			}

			dispatch({
				type: ADD_MESSAGES_TO_TEAM_CONVERSATION,
				payload: {
					teamMessages: res.data.teamMessages,
					_teamConversation: res.data._teamConversation,
				},
			});
		} catch (err) {
			console.log(err);
		}
	};

// merge: true is the catch-up after a disconnect. It folds the fetched
// conversations into the loaded ones instead of replacing them.
export const getInitialTeamConversations = ({ merge = false } = {}) => async (dispatch, getState) => {
	const businessId = getState().auth?._id;
	if (!merge) dispatch({ type: 'team_conversations_load_state', payload: { businessId, status: 'loading' } });
	try {
		const res = await axios.post(
			`${getTeamsUrl(window.location.href)}/api/team/conversations`
		);

		const { data } = res;

		// A late result from a previous location must not replace this location.
		if (getState().auth?._id !== businessId) return { error: false, skipped: true };
		if (data.error || !Array.isArray(data.teamConversationsWithMessages)) {
			if (!merge) dispatch({ type: 'team_conversations_load_state', payload: { businessId, status: 'failed' } });
			return { error: true };
		}

		dispatch({
			type: SET_TEAM_CONVERSATIONS,
			payload: data.teamConversationsWithMessages,
			meta: { businessId, merge },
		});
	} catch (err) {
		if (!merge && getState().auth?._id === businessId) dispatch({ type: 'team_conversations_load_state', payload: { businessId, status: 'failed' } });
		console.log(err);
	}
};

// The server does not replay team messages missed while the socket was down,
// so refetch the conversation list and the newest page of the open conversation.
export const catchUpTeamConversations = () => async (dispatch, getState) => {
	const { _activeTeamConversation } = getState().teams;

	await Promise.all([
		dispatch(getInitialTeamConversations({ merge: true })),
		_activeTeamConversation &&
			dispatch(
				getTeamConversationsPreviousMessages({
					_teamConversation: _activeTeamConversation,
					earliestMessageDate: new Date().toISOString(),
				})
			),
	]);
};

export const getPreviousTeamConversations =
	({
		loadConversationsBefore,
		_loadConversationUpTillThisTeamConversation = null,
		isDirectMessage,
	}) =>
	async (dispatch) => {
		try {
			const res = await axios.post(
				`${getTeamsUrl(
					window.location.href
				)}/api/team/load-previous-conversations`,
				{
					loadConversationsBefore,
					_loadConversationUpTillThisTeamConversation,
					isDirectMessage,
				}
			);

			const { data } = res;

			if (data.error) {
				return { error: true };
			}

			const { previousTeamConversationsWithMessages } = data;

			dispatch({
				type: APPEND_TEAM_CONVERSATIONS,
				payload: {
					previousTeamConversationsWithMessages,
				},
			});
		} catch (err) {
			console.log(err);
		}
	};

export const markConversationAsRead =
	(conversation, _activeUser) => async (dispatch) => {
		try {
			const { _unreadUsers } = conversation;

			// remove the current user from the unread users
			const _unreadUsersWithoutActiveUser = (_unreadUsers || []).filter(
				(user) => user !== _activeUser
			);

			// Only the read state changes, so a stale copy of the conversation
			// cannot overwrite messages that arrived since it was read.
			dispatch({
				type: 'UPDATE_TEAM_CONVERSATION',
				payload: {
					_id: conversation._id,
					_unreadUsers: _unreadUsersWithoutActiveUser,
				},
			});

			const res = await axios.post(
				`${getTeamsUrl(
					window.location.href
				)}/api/team/mark-conversation-as-read`,
				{
					_teamConversation: conversation._id,
				}
			);

			const { data } = res;

			if (data.error) {
				return { error: true };
			}

			return { error: false };
		} catch (err) {
			console.log(err);
		}
	};

export const createVideoChatLink =
	({ _teamConversation }) =>
	async (dispatch) => {
		try {
			const res = await axios.post(
				`${getTeamsUrl(
					window.location.href
				)}/api/team/create-video-chat-link`,
				{ _teamConversation }
			);

			const { data } = res;

			if (data.error) {
				return { error: true };
			}

			dispatch({
				type: ADD_MESSAGE_TO_TEAM_CONVERSATION,
				payload: {
					teamMessage: data.teamMessage,
					isSending: false,
					teamConversation: data.teamConversation,
				},
			});
		} catch (err) {
			console.log(err);
		}
	};

export const setActiveTeamConversation = (_conversation) => (dispatch) => {
	dispatch({
		type: SET_ACTIVE_TEAM_CONVERSATION,
		payload: _conversation,
	});
};

export const changeConversationTitle =
	({ teamConversation, _business }) =>
	async (dispatch) => {
		try {
			const res = await axios.post(
				`${getTeamsUrl(
					window.location.href
				)}/api/team/change-conversation-title`,
				{ teamConversation, _business }
			);

			const { data } = res;

			if (data.error) {
				return { error: true };
			}

			dispatch({
				type: ADD_MESSAGE_TO_TEAM_CONVERSATION,
				payload: data.teamConversationWithMessagesAndNewTitle,
			});
		} catch (err) {
			console.log(err);
		}
	};

export const editNoteFromConversation =
	({ teamConversation, _business }) =>
	async (dispatch) => {
		try {
			const teamsApiUrl = getTeamsUrl(window.location.href);
			// Make the API call to update the note
			await axios.post(
				`${teamsApiUrl}/api/teams/edit-conversation-notes`,
				{
					teamConversation,
					_business,
				}
			);

			// Dispatch the action to update the state
			dispatch({
				type: ADD_MESSAGE_TO_TEAM_CONVERSATION,
				payload: { teamConversation, teamMessage: {} },
			});
		} catch (err) {
			console.log(err);
		}
	};
export const sendTeamMessage =
	({
		_teamConversation,
		text,
		_business,
		attachments,
		_attachedCustomer,
		_mentionedUsers,
		mentions, requestId,
		_replyTo,
	}) =>
	async (dispatch) => {
		try {
			const res = await axios.post(
				`${getTeamsUrl(window.location.href)}/api/team/send-message`,
				{
					_teamConversation,
					text,
					_business,
					attachments,
					_attachedCustomer,
					_mentionedUsers,
					mentions, requestId,
					_replyTo,
				}
			);

			const { data } = res;

			if (data.error) {
				return { error: true, message: data.message };
			}

			dispatch({
				type: ADD_MESSAGE_TO_TEAM_CONVERSATION,
				payload: {
					teamMessage: data.teamMessage,
					isSending: true,
					teamConversation: data.teamConversation,
				},
			});
			return { error: false };
		} catch (err) {
			return { error: true, latestNote: err.response?.data?.latestNote, message: err.response?.data?.message || "Could not save note." };
		}
	};

// message is the message being replied to, or null to stop replying
export const setTeamReply = ({ _teamConversation, message }) => ({
	type: SET_TEAM_REPLY,
	payload: { _teamConversation, message },
});

export const pinMessageToNotes =
	({ teamMessage }) =>
	async (dispatch) => {
		try {
			const { data } = await axios.post(
				`${getTeamsUrl(window.location.href)}/api/team/pin-message-to-notes`,
				// the notes card shows the date as it was written, in local time
				{ _teamMessage: teamMessage._id, dateLabel: moment().format('llll') }
			);

			if (data.error) {
				return { error: true, message: data.message };
			}

			dispatch({
				type: ADD_TEAM_CONVERSATION,
				payload: data.teamConversationWithNewNote,
			});

			return { error: false };
		} catch (err) {
			return { error: true, message: err.response?.data?.message };
		}
	};

export const setActiveTeamConversationToNull = () => async (dispatch) => {
	dispatch({
		type: SET_ACTIVE_TEAM_CONVERSATION_TO_NULL,
	});
};

export const getTeamConversationForCustomer =
	({ _customer, signal } = {}) =>
	async (dispatch) => {
		try {
			const res = await axios.get(
				`${getTeamsUrl(
					window.location.href
				)}/api/team/get-customer-conversation/${_customer}`,
				{ signal }
			);

			// an aborted request can come back without a conversation
			if (res.data?.error || !res.data?.teamConversationWithMessages?._id) {
				console.log(
					'error getting team conversation for customer',
					_customer,
					res.data
				);
				return { error: true };
			}

			const { data } = res;

			dispatch({
				type: ADD_TEAM_CONVERSATION,
				payload: data.teamConversationWithMessages,
			});

			dispatch({
				type: SET_ACTIVE_TEAM_CONVERSATION,
				payload: data.teamConversationWithMessages._id,
			});

			return {
				_teamConversation: data.teamConversationWithMessages._id,
				messageCount:
					data.teamConversationWithMessages?.teamMessages?.length ||
					0,
			};
		} catch (error) {
			if (error?.code === 'ERR_CANCELED') {
				return { error: true, aborted: true };
			}

			console.log(error);
			return { error: true };
		}
	};

export const createTeamConversationForCustomer =
	({ _customer, isRunner, title, _business = undefined, initialMessage = undefined, mentions = undefined, requestId = undefined }) =>
	async (dispatch) => {
		try {
			const response = await axios.post(
				`${getTeamsUrl(
					window.location.href
				)}/api/team/create-conversation-for-customer`,
				{
					title,
					_customer,
					isRunner,
					_business,
					initialMessage, mentions, requestId,
				}
			);

			if (response.data.error) {
				return { error: true, message: response.data.message };
			}

			const { data } = response;

			dispatch({
				type: ADD_TEAM_CONVERSATION,
				payload: data.teamConversationWithMessages,
			});

			dispatch({
				type: SET_ACTIVE_TEAM_CONVERSATION,
				payload: data.teamConversationWithMessages._id,
			});

			return { error: false };
		} catch (error) {
			console.log(error);
			return { error: true, latestNote: error.response?.data?.latestNote, message: error.response?.data?.message || "Could not save note." };
		}
	};

export const createTeamConversation =
	({ _users, title }) =>
	async (dispatch) => {
		try {
			const res = await axios.post(
				`${getTeamsUrl(
					window.location.href
				)}/api/team/create-conversation`,
				{ _users, title }
			);

			if (res.data.error) {
				return { error: true };
			}

			const { data } = res;

			dispatch({
				type: ADD_TEAM_CONVERSATION,
				payload: data.teamConversationWithMessages,
			});

			dispatch({
				type: SET_ACTIVE_TEAM_CONVERSATION,
				payload: data.teamConversationWithMessages._id,
			});

			// the caller opens the new conversation from this
			return { error: false, teamConversation: data.teamConversationWithMessages };
		} catch (err) {
			console.log(err);
			return { error: true };
		}
	};

// Socket events arrive for every location the user belongs to. A loaded
// conversation (for example another location's customer notepad) keeps
// updating, but only the current location's conversations are added.
const isLoadedOrCurrentLocation = (state, _teamConversation, _business) =>
	state.teams.teamConversations.some((c) => c._id === _teamConversation) ||
	(!!_business && String(_business) === String(state.auth?._id));

export const addMessageToConversation =
	({ teamMessage, teamConversation }) =>
	async (dispatch, getState) => {
		if (
			!isLoadedOrCurrentLocation(
				getState(),
				teamMessage?._teamConversation,
				teamConversation?._business
			)
		) {
			return;
		}

		dispatch({
			type: ADD_MESSAGE_TO_TEAM_CONVERSATION,
			payload: { teamMessage, teamConversation },
		});
	};

export const addTeamConversation =
	(teamConversationData) => async (dispatch, getState) => {
		if (
			!isLoadedOrCurrentLocation(
				getState(),
				teamConversationData?._id,
				teamConversationData?._business
			)
		) {
			return;
		}

		dispatch({
			type: ADD_TEAM_CONVERSATION,
			payload: teamConversationData,
		});
	};

export const getTeamConversationsPreviousMessages =
	({ _teamConversation, earliestMessageDate, refresh = false }) =>
	async (dispatch) => {
		try {
			const res = await axios.post(
				`${getTeamsUrl(window.location.href)}/api/team/conversation`,
				{
					_teamConversation,
					earliestMessageDate,
				}
			);

			if (res.data.error) {
				return { error: true };
			}

			// if refresh is true, replace all existing messages with new ones
			if (refresh) {
				return dispatch({
					type: REPLACE_MESSAGES_IN_TEAM_CONVERSATION,
					payload: {
						teamMessages: res.data.teamMessages,
						_teamConversation,
					},
				});
			}

			dispatch({
				type: ADD_MESSAGES_TO_TEAM_CONVERSATION,
				payload: {
					teamMessages: res.data.teamMessages,
					_teamConversation,
				},
			});

			return { hasMore: !!res.data.teamMessages.length };
		} catch (err) {
			console.log(err);
			return { error: true };
		}
	};

export const removeTeamConversation =
	({ conversation }) =>
	async (dispatch) => {
		try {
			const res = await axios.post(
				`${getTeamsUrl(
					window.location.href
				)}/api/team/leave-conversation`,
				{
					conversation,
					_business: conversation._business,
				}
			);

			if (res.data.error) {
				return { error: true };
			}

			dispatch({
				type: REMOVE_TEAM_CONVERSATION,
				payload: {
					_teamConversation:
						res.data.teamConversationWithRemovedUser._id,
				},
			});
		} catch (err) {
			console.log(err);
		}
	};
/**
 * Permanently deletes a group chat for every member.
 */
export const deleteTeamConversation =
	({ _teamConversation }) =>
	async (dispatch) => {
		try {
			// Failures are surfaced by the caller, so the global error toast is skipped
			const res = await axios.delete(
				`${getTeamsUrl(
					window.location.href
				)}/api/team/conversation/${_teamConversation}`,
				{ _suppressErrorToast: true }
			);

			if (res.data.error) {
				return { error: true, message: res.data.message };
			}

			dispatch({
				type: REMOVE_TEAM_CONVERSATION,
				payload: { _teamConversation },
			});

			return { error: false };
		} catch (err) {
			console.log(err);
			return {
				error: true,
				message: err?.response?.data?.message,
			};
		}
	};

/**
 * Removes a conversation from local state only. Used when another member
 * deletes a group and the removal arrives over the socket.
 */
export const removeTeamConversationFromState =
	({ _teamConversation }) =>
	(dispatch) => {
		dispatch({
			type: REMOVE_TEAM_CONVERSATION,
			payload: { _teamConversation },
		});
	};

export const addUsersToConversation =
	({ _teamConversation, users, _business }) =>
	async (dispatch) => {
		try {
			const res = await axios.post(
				`${getTeamsUrl(
					window.location.href
				)}/api/team/add-user-to-conversation`,
				{
					_teamConversation,
					users,
					_business,
				}
			);
			if (res.data.error) {
				return { error: true };
			}

			const { data } = res;

			dispatch({
				type: ADD_TEAM_CONVERSATION,
				payload: data.teamConversationWithAddedUsers,
			});
		} catch (err) {
			console.log(err);
		}
	};

export const toggleCurrentConversationMute =
	({ _teamConversation, _business, isCurrentConversationMuted }) =>
	async (dispatch) => {
		try {
			const res = await axios.post(
				`${getTeamsUrl(
					window.location.href
				)}/api/team/toggle-current-conversation-mute`,
				{
					_teamConversation,
					_business,
					isCurrentConversationMuted,
				}
			);

			if (res.data.error) {
				return { error: true };
			}

			const { data } = res;

			dispatch({
				type: UPDATE_TEAM_CONVERSATION,
				payload: data.updatedTeamConversation,
			});
		} catch (err) {
			console.log(err);
		}
	};

export const toggleCurrentConversationStarred =
	({ _teamConversation, _business, isCurrentConversationStarred }) =>
	async (dispatch) => {
		try {
			const res = await axios.post(
				`${getTeamsUrl(
					window.location.href
				)}/api/team/toggle-current-conversation-starred`,
				{
					_teamConversation,
					_business,
					isCurrentConversationStarred,
				}
			);

			if (res.data.error) {
				return { error: true };
			}

			const { data } = res;

			dispatch({
				type: UPDATE_TEAM_CONVERSATION,
				payload: data.updatedTeamConversation,
			});
		} catch (err) {
			console.log(err);
		}
	};

export const updateReactionOnTeamMessage =
	({ message, updatedTeamMessage, emoji, isReactionAddedToMessage }) =>
	async (dispatch) => {
		// Puts the message back as it was before the optimistic update
		const rollback = () =>
			dispatch({
				type: UPDATE_TEAM_MESSAGE,
				payload: {
					updatedTeamMessage: {
						...message,
						reactions: message.reactions || [],
					},
				},
			});

		try {
			dispatch({
				type: UPDATE_TEAM_MESSAGE,
				payload: { updatedTeamMessage },
			});

			const res = await axios.post(
				`${getTeamsUrl(
					window.location.href
				)}/api/team/update-reaction-to-message`,
				{
					_teamMessage: updatedTeamMessage._id,
					emoji,
					isReactionAddedToMessage,
				}
			);

			if (res.data.error) {
				rollback();
			}
		} catch (err) {
			console.log(err);
			rollback();
		}
	};

export const updateTeamMessage =
	({ updatedTeamMessage }) =>
	async (dispatch) => {
		try {
			dispatch({
				type: UPDATE_TEAM_MESSAGE,
				payload: { updatedTeamMessage },
			});
		} catch (err) {
			console.log(err);
		}
	};

export const editTeamMessage =
	({ updatedTeamMessage }) =>
	async (dispatch) => {
		try {
			const res = await axios.post(
				`${getTeamsUrl(window.location.href)}/api/team/update-message`,
				{
					updatedTeamMessage,
				}
			);

			if (!res.data.error) {
				dispatch({
					type: UPDATE_TEAM_MESSAGE,
					payload: { updatedTeamMessage: res.data.updatedTeamMessage },
				});
			}
			return res.data;
		} catch (err) {
			return { error: true, latestNote: err.response?.data?.latestNote, message: err.response?.data?.message || "Could not save note." };
		}
	};

export const deleteTeamMessage =
	({ teamMessage }) =>
	async (dispatch) => {
		try {
			const res = await axios.post(
				`${getTeamsUrl(window.location.href)}/api/team/delete-message`,
				{
					teamMessage,
				}
			);

			if (!res.data.error) {
				dispatch({
					type: UPDATE_TEAM_MESSAGE,
					payload: {
						updatedTeamMessage: res.data.updatedTeamMessage,
					},
				});
			}
		} catch (err) {
			console.log(err);
		}
	};
